import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { useMutation } from '@tanstack/react-query';

/**
 * Interface for a single multiple-choice option
 */
interface MultipleChoiceOption {
  content: string;
  correct: boolean;
}

/**
 * Interface for a single multiple-choice question
 */
export interface MultipleChoiceQuestion {
  question_content: string;
  image: string;
  image_description: string;
  description: string;
  question_type: 'multiple_choice';
  points_obtainable: number;
  required: boolean;
  maximum_selections: number;
  options: MultipleChoiceOption[];
}

/**
 * Interface for multiple-choice question payload
 */
export interface MultipleChoiceQuestionPayload {
  assessment_id: string;
  question1: MultipleChoiceQuestion | null;
  question2: MultipleChoiceQuestion | null;
  question3: MultipleChoiceQuestion | null;
}

/**
 * Hook for posting multiple-choice questions
 */
export const useMultipleChoiceQuestionPost = () => {
  const { openToast } = useToast();

  return useMutation({
    mutationFn: async (payload: MultipleChoiceQuestionPayload) => {
      const response = await apiClient.post('/assessment/multiple-choice-question/', payload);
      return response.data;
    },
    onSuccess: () => {
      openToast({
        show: true,
        heading: "Success",
        text: "Multiple choice question(s) created successfully!",
        type: "info",
        timeOut: 5000,
      });
    },
    onError: (err: any) => {
      const errorMessage = err?.response?.data?.message || 'Failed to submit multiple-choice questions';
      openToast({
        show: true,
        heading: "Submission Failed",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });
};

